import {
  Card,
  Stack,
  Table,
  Button,
  TableBody,
  Container,
  Typography,
  TableContainer,
} from '@mui/material';
import { useMemo, useState, useCallback } from 'react';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import TableEmptyRows from 'src/components/table/table-empty-rows';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableNoData from 'src/components/table/table-no-data';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';
import {
  InviteCodeType,
  InviteCodeStatus,
  InviteCodeFilters,
  InviteCodeFilterKey,
  InviteCodeBadgeType,
  InviteCodeFilterValue,
} from 'src/type/invite-code';
import StatusTabFilter, { StatusTabFilterOption } from 'src/components/ui-kit/status-tab-filter';
import { useBoolean } from 'src/hooks/use-boolean';
import { toastError } from 'src/utils/format-error';
import { useDeepEffect } from 'src/hooks/use-deep-effect';
import axios, { endpoints } from 'src/utils/axios';
import { Pagination } from 'src/type/utils';
import { emptyRows } from 'src/components/table/utils';
import TableLoading from 'src/components/table/table-loading';
import { fParams, ParamsType } from 'src/utils/format-params';
import usePagination from 'src/hooks/use-pagination';
import InviteCodeTableRow from '../invite-code-table-row';

const defaultFilters: InviteCodeFilters = {
  status: '',
};

const tableHead = [
  { id: 'code', label: 'รหัส' },
  { id: 'createdAt', label: 'วันที่สร้าง', width: 180 },
  { id: 'usedAt', label: 'วันที่ใช้งาน', width: 180 },
  { id: 'createdBy', label: 'สร้างโดย', width: 180 },
  { id: 'status', label: 'สถานะ', width: 80 },
  { id: 'action', width: 60 },
];

export default function InviteCodeView() {
  const initialize = useBoolean(true);
  const reject = useBoolean(false);

  const [filters, setFilters] = useState(defaultFilters);
  const [badge, setBadge] = useState<InviteCodeBadgeType>({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });

  const {
    currentPage,
    prevPage,
    rowsPerPage,
    meta,
    onSetData,
    onResetPage,
    onChangePage,
    onChangeRowsPerPage,
    data: tableData,
  } = usePagination<InviteCodeType>();

  useDeepEffect(() => {
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      status: filters.status,
      page: currentPage,
      limit: rowsPerPage,
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
    };
    hanldeGetInviteCode(params);
  }, [currentPage, rowsPerPage, filters.status]);

  const hanldeGetInviteCode = useCallback(async (params: ParamsType, withoutLoader?: boolean) => {
    try {
      if (!withoutLoader) {
        initialize.onTrue();
      }
      const { data: _badge } = await axios.get<InviteCodeBadgeType>(endpoints.referrals.badges);
      setBadge(_badge);
      const { data } = await axios.get<Pagination<InviteCodeType>>(endpoints.referrals.get, {
        params: fParams(params),
      });
      onSetData(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  }, []);

  const handleFilters = useCallback((name: InviteCodeFilterKey, value: InviteCodeFilterValue) => {
    onResetPage();
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const onRejectCode = useCallback(
    async (id: string) => {
      if (reject.value) return;
      try {
        reject.onTrue();
        await axios.patch(endpoints.referrals.reject(id));

        const params = {
          status: filters.status,
          page: currentPage,
          limit: rowsPerPage,
          lastDocId: meta.prevDocId,
        };

        await hanldeGetInviteCode(params, true);
      } catch (error) {
        toastError(error);
      } finally {
        reject.onFalse();
      }
    },
    [meta]
  );

  const tabs: StatusTabFilterOption = useMemo(
    () => [
      { value: '', label: 'ทั้งหมด', color: 'default', count: badge.total },
      {
        value: InviteCodeStatus.ACCEPTED,
        label: 'ใช้งานแล้ว',
        color: 'success',
        count: badge.approved,
      },
      {
        value: InviteCodeStatus.PENDING,
        label: 'ยังไม่ใช้งาน',
        color: 'warning',
        count: badge.pending,
      },
      {
        value: InviteCodeStatus.REJECTED,
        label: 'ยกเลิก',
        color: 'error',
        count: badge.rejected,
      },
    ],
    [badge]
  );

  return (
    <Container>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h4" gutterBottom>
            รหัสเชิญ
          </Typography>
          <Button
            component={RouterLink}
            href={paths.management.inviteCode.create}
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            สร้างรหัสเชิญ
          </Button>
        </Stack>

        <Card>
          <StatusTabFilter
            value={filters.status}
            onChange={(newValue) => handleFilters('status', newValue)}
            options={tabs}
          />

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table sx={{ minWidth: 800 }}>
                <TableHeadCustom headLabel={tableHead} />

                <TableBody>
                  <TableLoading loading={initialize.value} rows={rowsPerPage}>
                    {tableData.map((row) => (
                      <InviteCodeTableRow
                        key={row.id}
                        row={row}
                        onRejectCode={() => onRejectCode(row.id)}
                      />
                    ))}

                    <TableEmptyRows
                      show={!initialize.value}
                      height={76}
                      emptyRows={emptyRows(currentPage, rowsPerPage, meta.totalResults)}
                    />

                    <TableNoData notFound={!tableData.length} />
                  </TableLoading>
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={meta.totalResults}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
          />
        </Card>
      </Stack>
    </Container>
  );
}
